"use client"

import type React from "react"
import { createContext, useState, useEffect, type ReactNode } from "react"
import { AuthApi } from "@/infrastructure/api/Auth"
import type { User } from "@/domain/entities/User"
import type { LoginData, LoginRequest, LoginResponse } from "@/domain/interfaces/auth"
import type { UserRole } from "@/domain/enums/roles"

export interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (credentials: LoginRequest) => Promise<LoginResponse>
  logout: () => void
  hasRole: (role: UserRole) => boolean
  hasAnyRole: (roles: UserRole[]) => boolean
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

const TOKEN_STORAGE_KEY = "authToken"
const USER_STORAGE_KEY = "userData"
const TOKEN_EXPIRY_KEY = "tokenExpiry"

const isTokenExpired = (expiresIn: string): boolean => {
  try {
    const expiryDate = new Date(expiresIn)
    const now = new Date()
    return now >= expiryDate
  } catch {
    return true
  }
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = !!user

  const getStoredAuth = (): { user: User; token: string; expiresIn: string } | null => {
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY)
      const userData = localStorage.getItem(USER_STORAGE_KEY)
      const expiresIn = localStorage.getItem(TOKEN_EXPIRY_KEY)

      if (token && userData && expiresIn) {
        const user = JSON.parse(userData) as User
        return { user, token, expiresIn }
      }
    } catch (error) {
      console.error("Error parsing stored auth data:", error)
      clearStoredAuth()
    }

    return null
  }

  const clearStoredAuth = () => {
    localStorage.removeItem(TOKEN_STORAGE_KEY)
    localStorage.removeItem(USER_STORAGE_KEY)
    localStorage.removeItem(TOKEN_EXPIRY_KEY)
  }

  const refreshTokenIfNeeded = async (): Promise<boolean> => {
    const storedAuth = getStoredAuth()
    if (!storedAuth) return false

    if (isTokenExpired(storedAuth.expiresIn)) {
      try {
        const refreshResponse = await AuthApi.refreshToken()

        if (refreshResponse.isSuccess && refreshResponse.data) {
          localStorage.setItem(TOKEN_STORAGE_KEY, refreshResponse.data.accessToken)
          localStorage.setItem(TOKEN_EXPIRY_KEY, refreshResponse.data.expiresIn)
          return true
        } else {
          // Refresh failed, logout user
          await logout()
          return false
        }
      } catch (error) {
        console.error("Token refresh failed:", error)
        await logout()
        return false
      }
    }

    return true
  }

  const hasRole = (role: UserRole): boolean => {
    return user?.role === role
  }

  const hasAnyRole = (roles: UserRole[]): boolean => {
    return user ? roles.includes(user.role) : false
  }

  const _handleLoginResponse = (responseData: LoginData) => {
    setUser(responseData.user)
    localStorage.setItem(TOKEN_STORAGE_KEY, responseData.accessToken)
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(responseData.user))
    localStorage.setItem(TOKEN_EXPIRY_KEY, responseData.expiresIn)
  }

  const login = async (credentials: LoginRequest) => {
    setIsLoading(true)
    try {
      let response

      if (credentials.type === "password") {
        response = await AuthApi.login(credentials)
        if (!response.isSuccess) {
          throw new Error(response.message || "Login failed")
        }
      } else if (credentials.type === "otp") {
        response = await AuthApi.confirmOtp(credentials)
        if (!response.isSuccess) {
          throw new Error(response.message || "Login failed")
        }
      } else {
        throw new Error("Invalid login type")
      }

      if (response.isSuccess && response.data) {
        _handleLoginResponse(response.data)
      } else {
        throw new Error(response.message || "Login failed")
      }

      return response
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    try {
      setIsLoading(true)
      await AuthApi.logout()
    } catch (error) {
      console.error("Logout error:", error)
    } finally {
      setUser(null)
      clearStoredAuth()
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const initializeAuth = async () => {
      const storedAuth = getStoredAuth()
      if (storedAuth) {
        const tokenValid = await refreshTokenIfNeeded()
        if (tokenValid) {
          setUser(storedAuth.user)
        }
      }
      setIsLoading(false)
    }

    initializeAuth()
  }, [])

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    hasRole,
    hasAnyRole,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
